// src/pages/trading-portal/VolatilityArbitrage/StrategyConstructionPage.js
// Strategy Construction - tabbed interface for strategies and dispersion trades

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Tabs,
    Tab,
    Tooltip,
    Skeleton,
    Snackbar,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayArrowIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    AccountTree as AccountTreeIcon,
    ScatterPlot as ScatterPlotIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    VAEStrategyType,
    VAEStrategyTypeLabels,
    VAEStrategyTypeColors,
    VAEStrategyStatus,
    VAEStrategyStatusLabels,
    VAEStrategyStatusColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const INITIAL_STRATEGY_FORM = {
    name: '',
    strategy_type: '',
    symbol: '',
    entry_iv: '',
    target_profit: '',
    stop_loss: '',
    max_loss: '',
    notional_value: '',
};

const StrategyConstructionPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Global state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState(null);

    // ==================== TAB 0: STRATEGIES ====================
    const [strategies, setStrategies] = useState([]);
    const [strategiesLoading, setStrategiesLoading] = useState(true);
    const [strategiesPage, setStrategiesPage] = useState(0);
    const [strategiesPerPage, setStrategiesPerPage] = useState(10);
    const [strategiesTotal, setStrategiesTotal] = useState(0);
    const [strategyDialog, setStrategyDialog] = useState(false);
    const [strategyForm, setStrategyForm] = useState({ ...INITIAL_STRATEGY_FORM });
    const [strategyEditing, setStrategyEditing] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [executeLoading, setExecuteLoading] = useState(null);

    // Filters
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterSymbol, setFilterSymbol] = useState('');

    // Legs dialog
    const [legsDialog, setLegsDialog] = useState(false);
    const [legsData, setLegsData] = useState([]);
    const [legsLoading, setLegsLoading] = useState(false);
    const [legsStrategyName, setLegsStrategyName] = useState('');

    // ==================== PERMISSIONS ====================
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-vae-strategy-construction', 'view');
            const add = await hasPermission('tr-vae-strategy-construction', 'add');
            const edit = await hasPermission('tr-vae-strategy-construction', 'edit');
            const del = await hasPermission('tr-vae-strategy-construction', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // ==================== TAB 0: STRATEGIES ====================
    const fetchStrategies = useCallback(async () => {
        if (!canView) return;
        setStrategiesLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: strategiesPage + 1, per_page: strategiesPerPage });
            if (filterStatus) params.append('status', filterStatus);
            if (filterType) params.append('strategy_type', filterType);
            if (filterSymbol) params.append('symbol', filterSymbol);
            const response = await axiosInstance.get(`/api/volatility-arbitrage/strategies?${params.toString()}`);
            if (response.data.success) {
                setStrategies(response.data.data || []);
                setStrategiesTotal(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch strategies');
        } finally {
            setStrategiesLoading(false);
        }
    }, [canView, strategiesPage, strategiesPerPage, filterStatus, filterType, filterSymbol]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchStrategies();
    }, [canView, activeTab, fetchStrategies]);

    const handleOpenStrategyDialog = (strategy = null) => {
        if (strategy) {
            setStrategyEditing(strategy);
            setStrategyForm({
                name: strategy.name || '',
                strategy_type: strategy.strategy_type || '',
                symbol: strategy.symbol || '',
                entry_iv: strategy.entry_iv ?? '',
                target_profit: strategy.target_profit ?? '',
                stop_loss: strategy.stop_loss ?? '',
                max_loss: strategy.max_loss ?? '',
                notional_value: strategy.notional_value ?? '',
            });
        } else {
            setStrategyEditing(null);
            setStrategyForm({ ...INITIAL_STRATEGY_FORM });
        }
        setStrategyDialog(true);
    };

    const handleCloseStrategyDialog = () => {
        setStrategyDialog(false);
        setStrategyEditing(null);
        setStrategyForm({ ...INITIAL_STRATEGY_FORM });
    };

    const handleStrategyFormChange = (field, value) => {
        setStrategyForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveStrategy = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                name: strategyForm.name,
                strategy_type: strategyForm.strategy_type,
                symbol: strategyForm.symbol,
                entry_iv: strategyForm.entry_iv !== '' ? parseFloat(strategyForm.entry_iv) : null,
                target_profit: strategyForm.target_profit !== '' ? parseFloat(strategyForm.target_profit) : null,
                stop_loss: strategyForm.stop_loss !== '' ? parseFloat(strategyForm.stop_loss) : null,
                max_loss: strategyForm.max_loss !== '' ? parseFloat(strategyForm.max_loss) : null,
                notional_value: strategyForm.notional_value !== '' ? parseFloat(strategyForm.notional_value) : null,
            };
            if (strategyEditing) {
                await axiosInstance.put(`/api/volatility-arbitrage/strategies/${strategyEditing.id}`, payload);
                setSnackbar('Strategy updated successfully');
            } else {
                await axiosInstance.post('/api/volatility-arbitrage/strategies', payload);
                setSnackbar('Strategy created successfully');
            }
            handleCloseStrategyDialog();
            fetchStrategies();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save strategy');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteStrategy = async () => {
        if (!deleteTarget) return;
        setSubmitting(true);
        setError(null);
        try {
            await axiosInstance.delete(`/api/volatility-arbitrage/strategies/${deleteTarget.id}`);
            setSnackbar('Strategy deleted successfully');
            setDeleteDialog(false);
            setDeleteTarget(null);
            fetchStrategies();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete strategy');
        } finally {
            setSubmitting(false);
        }
    };

    const handleExecuteStrategy = async (strategy) => {
        setExecuteLoading(strategy.id);
        setError(null);
        try {
            await axiosInstance.post(`/api/volatility-arbitrage/strategies/${strategy.id}/execute`);
            setSnackbar('Strategy execution initiated');
            fetchStrategies();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to execute strategy');
        } finally {
            setExecuteLoading(null);
        }
    };

    const handleViewLegs = async (strategy) => {
        setLegsStrategyName(strategy.name);
        setLegsDialog(true);
        setLegsLoading(true);
        setLegsData([]);
        try {
            const response = await axiosInstance.get(`/api/volatility-arbitrage/strategies/${strategy.id}/legs`);
            if (response.data.success) {
                setLegsData(response.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch strategy legs');
        } finally {
            setLegsLoading(false);
        }
    };

    const handleCloseLegsDialog = () => {
        setLegsDialog(false);
        setLegsData([]);
        setLegsStrategyName('');
    };

    // ==================== RENDER HELPERS ====================
    const renderSkeletonRows = (colCount = 10) => (
        Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={`skel-${i}`}>
                {Array.from({ length: colCount }).map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                ))}
            </TableRow>
        ))
    );

    const formatNumber = (val, decimals = 2) => {
        if (val === null || val === undefined || val === '') return '-';
        return Number(val).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };

    const formatDate = (val) => {
        if (!val) return '-';
        return new Date(val).toLocaleString();
    };

    // ==================== TAB 0: STRATEGIES TABLE ====================
    const renderStrategiesTab = () => (
        <Box>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ fontFamily }}>Status</InputLabel>
                    <Select
                        value={filterStatus}
                        label="Status"
                        onChange={(e) => { setFilterStatus(e.target.value); setStrategiesPage(0); }}
                        sx={{ fontFamily }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(VAEStrategyStatus).map(([key, val]) => (
                            <MenuItem key={key} value={val}>{VAEStrategyStatusLabels[key]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel sx={{ fontFamily }}>Strategy Type</InputLabel>
                    <Select
                        value={filterType}
                        label="Strategy Type"
                        onChange={(e) => { setFilterType(e.target.value); setStrategiesPage(0); }}
                        sx={{ fontFamily }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(VAEStrategyType).map(([key, val]) => (
                            <MenuItem key={key} value={val}>{VAEStrategyTypeLabels[key]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Symbol"
                    value={filterSymbol}
                    onChange={(e) => { setFilterSymbol(e.target.value); setStrategiesPage(0); }}
                    sx={{ minWidth: 140, '& .MuiInputBase-root': { fontFamily } }}
                    InputLabelProps={{ sx: { fontFamily } }}
                />
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchStrategies} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
                {canAdd && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenStrategyDialog()}
                        sx={{ fontFamily, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, textTransform: 'none' }}
                    >
                        Add Strategy
                    </Button>
                )}
            </Box>

            {/* Table */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                            {['Name', 'Type', 'Symbol', 'Status', 'Entry IV', 'Current IV', 'Target Profit', 'Stop Loss', 'Notional', 'Opened At', 'Actions'].map((h) => (
                                <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {strategiesLoading ? renderSkeletonRows(11) : strategies.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4, fontFamily, color: '#999' }}>
                                    No strategies found.
                                </TableCell>
                            </TableRow>
                        ) : strategies.map((s) => (
                            <TableRow key={s.id} hover>
                                <TableCell sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600 }}>{s.name || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={VAEStrategyTypeLabels[s.strategy_type] || s.strategy_type}
                                        color={VAEStrategyTypeColors[s.strategy_type] || 'default'}
                                        size="small"
                                        sx={{ fontFamily, fontSize: '0.75rem' }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600 }}>{s.symbol || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={VAEStrategyStatusLabels[s.status] || s.status}
                                        color={VAEStrategyStatusColors[s.status] || 'default'}
                                        size="small"
                                        sx={{ fontFamily, fontSize: '0.75rem' }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{formatNumber(s.entry_iv)}</TableCell>
                                <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{formatNumber(s.current_iv)}</TableCell>
                                <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{formatNumber(s.target_profit)}</TableCell>
                                <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{formatNumber(s.stop_loss)}</TableCell>
                                <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{formatNumber(s.notional_value)}</TableCell>
                                <TableCell sx={{ fontFamily, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{formatDate(s.opened_at)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {s.status === 'DRAFT' && canEdit && (
                                        <Tooltip title="Execute Strategy">
                                            <IconButton
                                                size="small"
                                                color="success"
                                                onClick={() => handleExecuteStrategy(s)}
                                                disabled={executeLoading === s.id}
                                            >
                                                {executeLoading === s.id ? <CircularProgress size={18} /> : <PlayArrowIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="View Legs">
                                        <IconButton size="small" onClick={() => handleViewLegs(s)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {canEdit && (
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleOpenStrategyDialog(s)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canDelete && (
                                        <Tooltip title="Delete">
                                            <IconButton size="small" color="error" onClick={() => { setDeleteTarget(s); setDeleteDialog(true); }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={strategiesTotal}
                page={strategiesPage}
                onPageChange={(_, p) => setStrategiesPage(p)}
                rowsPerPage={strategiesPerPage}
                onRowsPerPageChange={(e) => { setStrategiesPerPage(parseInt(e.target.value, 10)); setStrategiesPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ fontFamily }}
            />
        </Box>
    );

    // ==================== TAB 1: DISPERSION TRADES ====================
    const renderDispersionTab = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <ScatterPlotIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" sx={{ fontFamily, fontWeight: 600, color: '#555', mb: 1 }}>
                Dispersion Trades
            </Typography>
            <Typography variant="body2" sx={{ fontFamily, color: '#999', maxWidth: 480, textAlign: 'center' }}>
                Dispersion trade configurations will be managed through the Strategy Builder when using the Dispersion strategy type.
            </Typography>
        </Box>
    );

    // ==================== MAIN RENDER ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>
                    You do not have permission to view Strategy Construction.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, fontFamily }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccountTreeIcon sx={{ fontSize: 28, color: ACCENT, mr: 1.5 }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>
                    Strategy Construction
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                    mb: 3,
                    '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600 },
                    '& .Mui-selected': { color: ACCENT },
                    '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                }}
            >
                <Tab label="Strategies" />
                <Tab label="Dispersion Trades" />
            </Tabs>

            {/* Tab Content */}
            {activeTab === 0 && renderStrategiesTab()}
            {activeTab === 1 && renderDispersionTab()}

            {/* ==================== ADD / EDIT STRATEGY DIALOG ==================== */}
            <Dialog open={strategyDialog} onClose={handleCloseStrategyDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {strategyEditing ? 'Edit Strategy' : 'Add Strategy'}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                fullWidth
                                size="small"
                                value={strategyForm.name}
                                onChange={(e) => handleStrategyFormChange('name', e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontFamily }}>Strategy Type</InputLabel>
                                <Select
                                    value={strategyForm.strategy_type}
                                    label="Strategy Type"
                                    onChange={(e) => handleStrategyFormChange('strategy_type', e.target.value)}
                                    sx={{ fontFamily }}
                                >
                                    {Object.entries(VAEStrategyType).map(([key, val]) => (
                                        <MenuItem key={key} value={val} sx={{ fontFamily }}>{VAEStrategyTypeLabels[key]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Symbol"
                                fullWidth
                                size="small"
                                value={strategyForm.symbol}
                                onChange={(e) => handleStrategyFormChange('symbol', e.target.value.toUpperCase())}
                                sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Entry IV"
                                fullWidth
                                size="small"
                                type="number"
                                value={strategyForm.entry_iv}
                                onChange={(e) => handleStrategyFormChange('entry_iv', e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Target Profit"
                                fullWidth
                                size="small"
                                type="number"
                                value={strategyForm.target_profit}
                                onChange={(e) => handleStrategyFormChange('target_profit', e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Stop Loss"
                                fullWidth
                                size="small"
                                type="number"
                                value={strategyForm.stop_loss}
                                onChange={(e) => handleStrategyFormChange('stop_loss', e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Max Loss"
                                fullWidth
                                size="small"
                                type="number"
                                value={strategyForm.max_loss}
                                onChange={(e) => handleStrategyFormChange('max_loss', e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Notional Value"
                                fullWidth
                                size="small"
                                type="number"
                                value={strategyForm.notional_value}
                                onChange={(e) => handleStrategyFormChange('notional_value', e.target.value)}
                                sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseStrategyDialog} sx={{ fontFamily, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveStrategy}
                        disabled={submitting || !strategyForm.name || !strategyForm.strategy_type || !strategyForm.symbol}
                        sx={{ fontFamily, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, textTransform: 'none' }}
                    >
                        {submitting ? <CircularProgress size={20} /> : strategyEditing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ==================== DELETE CONFIRMATION DIALOG ==================== */}
            <Dialog open={deleteDialog} onClose={() => { setDeleteDialog(false); setDeleteTarget(null); }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete strategy <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => { setDeleteDialog(false); setDeleteTarget(null); }} sx={{ fontFamily, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteStrategy}
                        disabled={submitting}
                        sx={{ fontFamily, textTransform: 'none' }}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ==================== LEGS DIALOG ==================== */}
            <Dialog open={legsDialog} onClose={handleCloseLegsDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Strategy Legs - {legsStrategyName}</span>
                    <IconButton size="small" onClick={handleCloseLegsDialog}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {legsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: ACCENT }} />
                        </Box>
                    ) : legsData.length === 0 ? (
                        <Typography sx={{ fontFamily, color: '#999', textAlign: 'center', py: 4 }}>
                            No legs found for this strategy.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                                        {['Leg #', 'Type', 'Side', 'Symbol', 'Strike', 'Expiry', 'Quantity', 'Price', 'Status'].map((h) => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {legsData.map((leg, idx) => (
                                        <TableRow key={leg.id || idx} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{leg.option_type || leg.type || '-'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{leg.side || '-'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600 }}>{leg.symbol || '-'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{formatNumber(leg.strike)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{leg.expiry || leg.expiration || '-'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{leg.quantity ?? '-'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{formatNumber(leg.price)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>{leg.status || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>

            {/* ==================== SNACKBAR ==================== */}
            <Snackbar
                open={!!snackbar}
                autoHideDuration={3000}
                onClose={() => setSnackbar(null)}
                message={snackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </Box>
    );
};

export default StrategyConstructionPage;
